/**
 * Playground chat hook — consumes the Phase 3A-5 SSE endpoint
 * `POST /core/agent/unified-chat`.
 *
 * Dev-only surface for exercising any unified-runtime agent (starting with
 * `banan_unified`). Mirrors the shape of `useManagedAgentChat` at a high
 * level — produces a `UIMessage[]` compatible with `<ChatMessageList />` —
 * but is intentionally thin: no ads-context snapshot, no workspace store,
 * no custom-tool round-trip. The loop owns tool execution end-to-end.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { z } from "zod";
import { getAccessToken, resolveApiUrl } from "@/lib/jwt-auth-client";

/** Subset of NormalizedEvent this hook actually handles. Mirrors the Phase 3A-5 stream. */
const PlaygroundEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("session"), sessionId: z.string() }),
  z.object({ type: z.literal("text_delta"), text: z.string() }),
  z.object({ type: z.literal("thinking"), text: z.string().optional() }),
  z.object({
    type: z.literal("tool_use"),
    id: z.string(),
    name: z.string(),
    input: z.unknown().optional(),
  }),
  z.object({
    type: z.literal("tool_result"),
    id: z.string(),
    output: z.unknown().optional(),
    isError: z.boolean().optional(),
  }),
  z.object({
    type: z.literal("session_error"),
    errorType: z.string().optional(),
    message: z.string(),
    retryStatus: z.string().optional(),
  }),
  z.object({ type: z.literal("done") }),
]);
type PlaygroundEvent = z.infer<typeof PlaygroundEventSchema>;

function newId(role: "user" | "assistant"): string {
  return `${role}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Append a text part to the assistant message or start a new one. Mutates `parts` in place. */
function appendText(parts: UIMessage["parts"], text: string): void {
  const last = parts[parts.length - 1];
  if (last && last.type === "text") {
    last.text += text;
    return;
  }
  parts.push({ type: "text", text });
}

export interface InlineDefinitionPayload {
  agentName: string;
  unit: {
    id: string;
    name: string;
    display_name?: string;
    department?: string;
    orchestrator_agent_name?: string;
  };
  systemPrompt: string;
  modelId: string;
  imageModelId?: string;
  modelConfig?: Record<string, unknown>;
  toolNames: string[];
  skillNames: string[];
  maxSteps: number;
  sandboxProfile?: string;
}

export interface UsePlaygroundChatArgs {
  /** DB agent_name (e.g. `banan_unified`). */
  agentName: string;
  /** Optional — workspace context stored on the AgentRuntimeSession row. */
  workspaceId?: string;
  /**
   * Phase 4-6 resume: pre-populate sessionId + messages so the next `send`
   * continues an existing session instead of starting a fresh one. The
   * backend replays the full transcript into `streamText`; this hook just
   * paints the prior turns before streaming the new one.
   *
   * When either arg changes (user picks a different session), the hook
   * resyncs — aborts any in-flight stream, swaps state, readies to send.
   */
  initialSessionId?: string;
  initialMessages?: UIMessage[];
  /**
   * Phase 6-6: pin the conversation to a specific AgentDefinitionVersion
   * id (draft/staging/active). When omitted, the route falls back to the
   * agent's current active version (unchanged Phase 3 behavior).
   */
  versionId?: string | number;
  /**
   * Phase 24 §5 — when set, run the in-memory form state instead of a
   * persisted version. Mutually exclusive with `versionId`. The route
   * synthesizes an AgentDefinitionRow on the fly and tags the resulting
   * AgentRuntimeSession with an `inline_definition_hash`.
   */
  inlineDefinition?: InlineDefinitionPayload;
}

export interface UsePlaygroundChatResult {
  messages: UIMessage[];
  sessionId: string | undefined;
  isStreaming: boolean;
  error: string | undefined;
  /** Send a user message and consume the SSE response. */
  send: (text: string) => Promise<void>;
  /** Clear local message state + session id. Does not delete the server-side session row. */
  reset: () => void;
}

export function usePlaygroundChat({
  agentName,
  workspaceId,
  initialSessionId,
  initialMessages,
  versionId,
  inlineDefinition,
}: UsePlaygroundChatArgs): UsePlaygroundChatResult {
  const [messages, setMessages] = useState<UIMessage[]>(() => initialMessages ?? []);
  const [sessionId, setSessionId] = useState<string | undefined>(initialSessionId);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Keep a ref to the current assistant message id so SSE events can mutate it.
  const assistantIdRef = useRef<string | undefined>(undefined);

  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    assistantIdRef.current = undefined;
    setMessages([]);
    setSessionId(undefined);
    setIsStreaming(false);
    setError(undefined);
  }, []);

  // Resume-swap: when the caller swaps initialSessionId / initialMessages
  // (user picks a different session in the drawer, OR the transcript fetch
  // resolves a tick after the URL change lands), abort any in-flight
  // stream and re-seed.
  //
  // Two sync triggers:
  //   1. session id changed — full re-init, even if messages haven't
  //      arrived yet (they'll fall into trigger #2 on the next render).
  //   2. initialMessages arrived for an already-applied session id while
  //      local state is still empty — the "transcript loaded late" case.
  //
  // `lastAppliedIdRef` dedupes so we don't clobber the user's in-flight
  // conversation on every render where the parent re-passes the same
  // props.
  const lastAppliedIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (initialSessionId === undefined) return;
    const sessionChanged = lastAppliedIdRef.current !== initialSessionId;
    const messagesJustArrived =
      !sessionChanged &&
      messages.length === 0 &&
      (initialMessages?.length ?? 0) > 0;
    if (!sessionChanged && !messagesJustArrived) return;

    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    assistantIdRef.current = undefined;
    setMessages(initialMessages ?? []);
    setSessionId(initialSessionId);
    setIsStreaming(false);
    setError(undefined);
    lastAppliedIdRef.current = initialSessionId;
    // `messages.length` intentionally excluded — it changes as the user
    // chats, and we never want to overwrite a live conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId, initialMessages]);

  const applyEvent = useCallback(
    (event: PlaygroundEvent): void => {
      switch (event.type) {
        case "session":
          setSessionId(event.sessionId);
          return;
        case "text_delta":
          setMessages((prev) => {
            const id = assistantIdRef.current;
            if (!id) return prev;
            return prev.map((m) => {
              if (m.id !== id) return m;
              const parts = [...m.parts];
              appendText(parts, event.text);
              return { ...m, parts };
            });
          });
          return;
        case "thinking":
          setMessages((prev) => {
            const id = assistantIdRef.current;
            if (!id) return prev;
            const text =
              typeof event.text === "string" && event.text.length > 0
                ? event.text
                : "Thinking...";
            return prev.map((m) => {
              if (m.id !== id) return m;
              const existing = m.parts.find((p) => p.type === "reasoning");
              if (existing && existing.type === "reasoning") {
                existing.text = existing.text === "Thinking..." ? text : `${existing.text}${text}`;
                return { ...m, parts: [...m.parts] };
              }
              return {
                ...m,
                parts: [{ type: "reasoning", text }, ...m.parts],
              };
            });
          });
          return;
        case "tool_use":
          setMessages((prev) => {
            const id = assistantIdRef.current;
            if (!id) return prev;
            return prev.map((m) => {
              if (m.id !== id) return m;
              return {
                ...m,
                parts: [
                  ...m.parts,
                  {
                    type: `tool-${event.name}`,
                    toolCallId: event.id,
                    state: "input-available",
                    input: event.input ?? {},
                  } as UIMessage["parts"][number],
                ],
              };
            });
          });
          return;
        case "tool_result":
          setMessages((prev) => {
            const id = assistantIdRef.current;
            if (!id) return prev;
            return prev.map((m) => {
              if (m.id !== id) return m;
              return {
                ...m,
                parts: m.parts.map((p) => {
                  const pp = p as Record<string, unknown>;
                  if (pp.toolCallId === event.id) {
                    return {
                      ...pp,
                      state: event.isError ? "output-error" : "output-available",
                      output: event.output,
                    } as UIMessage["parts"][number];
                  }
                  return p;
                }),
              };
            });
          });
          return;
        case "session_error":
          setError(event.message);
          return;
        case "done":
          return;
      }
    },
    [],
  );

  const send = useCallback(
    async (text: string): Promise<void> => {
      if (isStreaming || !text.trim()) return;

      const userMsg: UIMessage = {
        id: newId("user"),
        role: "user",
        parts: [{ type: "text", text }],
      };
      const assistantMsg: UIMessage = {
        id: newId("assistant"),
        role: "assistant",
        parts: [],
      };
      assistantIdRef.current = assistantMsg.id;

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);
      setError(undefined);

      // getAccessToken is async — it auto-refreshes when the in-memory
      // token is expired. Calling it synchronously (earlier bug) shipped a
      // Bearer Promise to the server and always 401'd on long sessions.
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError("Not authenticated");
        setIsStreaming(false);
        return;
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await fetch(resolveApiUrl("/core/agent/unified-chat"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            agentName,
            userMessage: text,
            sessionId,
            workspaceId,
            isPlayground: true,
            // Phase 24 §5 — inlineDefinition is mutually exclusive with
            // versionId. Callers (the Workbench) should pass one or the
            // other, never both.
            ...(inlineDefinition !== undefined
              ? { inlineDefinition }
              : versionId !== undefined
                ? { versionId }
                : {}),
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const body = await response.text().catch(() => "");
          throw new Error(`HTTP ${response.status}: ${body || response.statusText}`);
        }
        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by "\n\n". A frame is "data: <json>".
          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? "";
          for (const frame of parts) {
            const trimmed = frame.trim();
            if (!trimmed) continue;
            const dataLine = trimmed
              .split("\n")
              .find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            const json = dataLine.slice(5).trim();
            try {
              const parsed = PlaygroundEventSchema.safeParse(JSON.parse(json));
              if (parsed.success) {
                applyEvent(parsed.data);
              } else {
                // eslint-disable-next-line no-console
                console.warn("playground: schema-mismatched SSE frame", json, parsed.error);
              }
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn("playground: unparseable SSE frame", json, err);
            }
          }
        }
      } catch (err) {
        if (controller.signal.aborted) {
          // user-initiated
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = undefined;
      }
    },
    [
      agentName,
      applyEvent,
      isStreaming,
      sessionId,
      workspaceId,
      versionId,
      inlineDefinition,
    ],
  );

  return { messages, sessionId, isStreaming, error, send, reset };
}
